/**
 * AppsList - Organism component for displaying paginated applications list
 */
import React, { useState, useEffect } from 'react';
import { AppData, useApiService } from '../../services/apiService';
import AppCard from '../molecules/AppCard';
import { Button } from '../atoms/Button';
import { ErrorMessage } from '../atoms/ErrorMessage';

interface AppsListProps {
  onAppClick?: (app: AppData) => void;
  compact?: boolean;
  showFilters?: boolean;
  showPagination?: boolean;
  defaultPageSize?: number;
  className?: string;
}

interface AppsFilters {
  app_type?: string;
  active_only?: boolean;
  search?: string;
}

interface AppsSortOptions {
  sort_by: 'name' | 'feedback_count' | 'last_feedback_at' | 'created_at';
  order: 'asc' | 'desc';
}

type ViewMode = 'grid' | 'list';

const AppsList: React.FC<AppsListProps> = ({
  onAppClick,
  compact = false,
  showFilters = true,
  showPagination = true,
  defaultPageSize = 12,
  className = ''
}) => {
  const apiService = useApiService();
  
  // State management
  const [apps, setApps] = useState<AppData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AppsFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortOptions, setSortOptions] = useState<AppsSortOptions>({ sort_by: 'name', order: 'asc' });
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Client-side sorting function
  const sortApps = (apps: AppData[], sortOptions: AppsSortOptions): AppData[] => {
    return [...apps].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortOptions.sort_by) {
        case 'name':
          aValue = (a.name || a.display_name || '').toLowerCase();
          bValue = (b.name || b.display_name || '').toLowerCase();
          break;
        case 'feedback_count':
          aValue = a.feedback_count || 0;
          bValue = b.feedback_count || 0;
          break;
        case 'last_feedback_at':
          aValue = a.last_feedback_at ? new Date(a.last_feedback_at).getTime() : 0;
          bValue = b.last_feedback_at ? new Date(b.last_feedback_at).getTime() : 0;
          break;
        case 'created_at':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortOptions.order === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOptions.order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Fetch apps data
  const fetchApps = async (page = 1, currentFilters = filters, currentPageSize = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getApps({
        ...currentFilters,
        skip: (page - 1) * currentPageSize,
        limit: currentPageSize
      });
      
      const sortedApps = sortApps(response.data.apps, sortOptions);
      setApps(sortedApps);
      setTotal(response.data.pagination.total);
      setTotalPages(response.data.pagination.pages);
      setCurrentPage(response.data.pagination.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications');
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchApps();
  }, []);

  // Re-sort when sort options change
  useEffect(() => {
    if (apps.length > 0) {
      const sortedApps = sortApps(apps, sortOptions);
      setApps(sortedApps);
    }
  }, [sortOptions]);

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<AppsFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    setCurrentPage(1);
    fetchApps(1, updatedFilters);
  };

  // Handle search
  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    handleFilterChange({ search: search || undefined });
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchApps(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchApps(1, filters, newPageSize);
  };

  // Handle sort change
  const handleSortChange = (newSortOptions: Partial<AppsSortOptions>) => {
    const updatedSort = { ...sortOptions, ...newSortOptions };
    setSortOptions(updatedSort);
    const sortedApps = sortApps(apps, updatedSort);
    setApps(sortedApps);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setSortOptions({ sort_by: 'name', order: 'asc' });
    setCurrentPage(1);
    fetchApps(1, {});
  };

  const appTypes = [
    { value: 'spa', label: 'Single Page App' },
    { value: 'native', label: 'Native/Mobile' },
    { value: 'regular_web', label: 'Web Application' },
    { value: 'machine_to_machine', label: 'M2M Application' }
  ];

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <ErrorMessage title="Error" message={error} />
        <Button 
          onClick={() => fetchApps()} 
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with count and search */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-stone-100">
            Applications
          </h2>
          <span className="text-sm text-grey-500 dark:text-grey-400" data-testid="apps-count">
            {total > 0 ? `${((currentPage - 1) * pageSize) + 1}-${Math.min(currentPage * pageSize, total)} of ${total}` : 'No applications found'}
          </span>
        </div>
        
        {showFilters && (
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="px-3 py-2 border border-grey-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-stone-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <Button
              onClick={clearFilters}
              variant="secondary"
              size="sm"
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      {/* Filters and Controls */}
      {showFilters && (
        <div className="space-y-4">
          {/* Main Filter Bar */}
          <div className="flex flex-wrap gap-3 p-4 bg-stone-200 dark:bg-slate-800 rounded-lg">
            {/* App Type Filter */}
            <select
              value={filters.app_type || ''}
              onChange={(e) => handleFilterChange({ app_type: e.target.value || undefined })}
              className="px-3 py-2 border border-grey-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-stone-100 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Types</option>
              {appTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            {/* Active Only Filter */}
            <label className="flex items-center space-x-2 text-sm text-slate-700 dark:text-stone-300">
              <input
                type="checkbox"
                checked={filters.active_only || false}
                onChange={(e) => handleFilterChange({ active_only: e.target.checked || undefined })}
                className="rounded border-grey-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500"
              />
              <span>Active only</span>
            </label>

            {/* Sort Controls */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-slate-700 dark:text-stone-300">Sort by:</label>
              <select
                value={sortOptions.sort_by}
                onChange={(e) => handleSortChange({ sort_by: e.target.value as AppsSortOptions['sort_by'] })}
                className="px-3 py-2 border border-grey-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-stone-100 text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="name">Name</option>
                <option value="feedback_count">Feedback Count</option>
                <option value="last_feedback_at">Last Feedback</option>
                <option value="created_at">Created Date</option>
              </select>
              <button
                onClick={() => handleSortChange({ order: sortOptions.order === 'asc' ? 'desc' : 'asc' })}
                className="px-3 py-2 border border-grey-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-stone-100 text-sm hover:bg-stone-200 dark:hover:bg-slate-600 focus:ring-2 focus:ring-primary-500"
                title={`Sort ${sortOptions.order === 'asc' ? 'descending' : 'ascending'}`}
              >
                {sortOptions.order === 'asc' ? '↑' : '↓'}
              </button>
            </div>

            {/* Page Size */}
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className="px-3 py-2 border border-grey-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-stone-100 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value={6}>6 per page</option>
              <option value={12}>12 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
            </select>
          </div>

          {/* View Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <span className="text-sm text-slate-700 dark:text-stone-300 mr-2">View:</span>
              <button
                onClick={() => handleViewModeChange('grid')}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-stone-100 border border-grey-300 dark:border-slate-600 hover:bg-stone-200 dark:hover:bg-slate-600'
                }`}
              >
                ⊞ Grid
              </button>
              <button
                onClick={() => handleViewModeChange('list')}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-stone-100 border border-grey-300 dark:border-slate-600 hover:bg-stone-200 dark:hover:bg-slate-600'
                }`}
              >
                ☰ List
              </button>
            </div>
            
            {/* Active Filters Indicator */}
            {(filters.app_type || filters.active_only || filters.search || sortOptions.sort_by !== 'name' || sortOptions.order !== 'asc') && (
              <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-stone-400">
                <span>Filters active:</span>
                <div className="flex items-center space-x-1">
                  {filters.search && (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                      Search: {filters.search}
                    </span>
                  )}
                  {filters.app_type && (
                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                      Type: {appTypes.find(t => t.value === filters.app_type)?.label}
                    </span>
                  )}
                  {filters.active_only && (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full text-xs">
                      Active only
                    </span>
                  )}
                  {sortOptions.sort_by !== 'name' && (
                    <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded-full text-xs">
                      Sort: {sortOptions.sort_by} {sortOptions.order}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" role="status" aria-label="Loading"></div>
          <span className="ml-2 text-slate-600 dark:text-stone-400">Loading applications...</span>
        </div>
      )}

      {/* Apps Display */}
      {!loading && (
        <div className="space-y-4">
          {apps.length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-stone-400" data-testid="empty-state">
              <div className="text-4xl mb-2">📱</div>
              <div className="text-lg font-medium mb-1">No applications found</div>
              <div className="text-sm">Try adjusting your filters or search terms</div>
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? `grid gap-4 ${compact ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`
              : 'space-y-3'
            }>
              {apps.map((app) => (
                <AppCard
                  key={app.app_id}
                  app={app}
                  onClick={onAppClick}
                  compact={compact}
                  showStats={true}
                  viewMode={viewMode}
                  className="transition-all hover:shadow-lg"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Enhanced Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-grey-200 dark:border-slate-700 pt-4">
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              variant="secondary"
              size="sm"
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {/* Smart pagination - show first, last, and pages around current */}
              {currentPage > 3 && (
                <>
                  <Button
                    onClick={() => handlePageChange(1)}
                    variant="secondary"
                    size="sm"
                  >
                    1
                  </Button>
                  {currentPage > 4 && (
                    <span className="text-slate-500 dark:text-stone-400 px-2">...</span>
                  )}
                </>
              )}
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const pageNum = startPage + i;
                if (pageNum > totalPages) return null;
                
                const isCurrentPage = currentPage === pageNum;
                return (
                  <Button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    variant={isCurrentPage ? "primary" : "secondary"}
                    size="sm"
                    className={isCurrentPage ? 'bg-primary-600 text-white' : ''}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="text-slate-500 dark:text-stone-400 px-2">...</span>
                  )}
                  <Button
                    onClick={() => handlePageChange(totalPages)}
                    variant="secondary"
                    size="sm"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>
            
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="sm"
            >
              Next
            </Button>
          </div>
          
          <div className="text-sm text-slate-500 dark:text-stone-400">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppsList;